import { getAuth } from "firebase/auth";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  orderBy,
  limit,
  collectionGroup,
  doc,
  getDoc,
} from "firebase/firestore";

let disasterDataCache = null;
let surveyDataCache = null;

const DisasterMgtService = {
  fetchDisasterData: async (disasterId, selectedCounties) => {
    try {
      const promises = [
        "general",
        "home_repair",
        "landscaping",
        "vehicle",
        "contents",
      ].map(async (collectionName) => {
        const q = query(
          collectionGroup(db, collectionName),
          where("associatedDisasters", "array-contains", disasterId),
          where("is_deleted", "==", false)
        );
        const snapshot = await getDocs(q);

        const docsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          const record = {
            collectionName,
            userId: data.userId,
            value: parseFloat(data.value || 0),
            ...data,
          };
          return record;
        });
        return docsData;
      });

      const results = await Promise.all(promises);
      let allRecords = results.flat();
      const userCountyChecks = allRecords.map(async (record) => {
        if (!record.userId) {
          return null;
        }

        try {
          const impactedAddressesRef = collection(
            db,
            "users",
            record.userId,
            "impacted-addresses"
          );
          const impactedAddressSnapshot = await getDocs(impactedAddressesRef);

          if (!impactedAddressSnapshot.empty) {
            for (const addrDoc of impactedAddressSnapshot.docs) {
              const addrData = addrDoc.data();
              const county = addrData.county;
              if (selectedCounties.includes(county)) {
                return record;
              }
            }
          } else {
            console.log(
              `No impacted addresses found for user ${record.userId}`
            );
          }
        } catch (error) {
          console.error(
            `Error fetching impacted addresses for user ${record.userId}:`,
            error
          );
        }
        return null;
      });

      const filteredRecords = (await Promise.all(userCountyChecks)).filter(
        Boolean
      );

      let totalValueSum = 0;
      let immediateAssistanceSum = 0;
      let recoveryAssistanceSum = 0;

      filteredRecords.forEach((record) => {
        totalValueSum += record.value;
        if (record.collectionName === "general") {
          immediateAssistanceSum += record.value;
        } else {
          recoveryAssistanceSum += record.value;
        }
      });
      const householdIdPromises = filteredRecords.map(async (record) => {
        try {
          const userDocRef = doc(db, "users", record.userId);
          const userSnapshot = await getDoc(userDocRef);
          if (userSnapshot.exists()) {
            const household_id = userSnapshot.data().household_id;
            return household_id;
          } else {
          }
        } catch (error) {
          console.error(
            "Error fetching household_id for user",
            record.userId,
            error
          );
        }
        return null;
      });
      const householdIds = (await Promise.all(householdIdPromises)).filter(
        Boolean
      );

      const uniqueHouseholdIds = new Set(householdIds);
      const totalRecords = uniqueHouseholdIds.size;

      disasterDataCache = {
        allRecords: filteredRecords,
        totalValueSum,
        immediateAssistanceSum,
        recoveryAssistanceSum,
        totalRecords,
      };
      console.log("Final disasterDataCache:", disasterDataCache);

      return disasterDataCache;
    } catch (error) {
      console.error("Error fetching disaster data:", error);
      throw error;
    }
  },

  fetchDisasterSurvey: async (disasterId, selectedCounties) => {
    try {
      const surveySnapshot = await getDocs(
        collectionGroup(db, "survey_responses")
      );

      const filteredResponses = surveySnapshot.docs.filter((doc) => {
        const docData = doc.data();
        return docData.disasters?.some((d) => d.disaster_id === disasterId);
      });

      const userCountyChecks = filteredResponses.map(async (surveyDoc) => {
        const data = surveyDoc.data();
        const userId = data.user_id;
        if (!userId) {
          return null;
        }

        try {
          const impactedAddressesRef = collection(
            db,
            "users",
            userId,
            "impacted-addresses"
          );
          const impactedAddressesSnapshot = await getDocs(impactedAddressesRef);
          const matchFound = impactedAddressesSnapshot.docs.some((doc) =>
            selectedCounties.includes(doc.data().county)
          );
          return matchFound ? surveyDoc : null;
        } catch (error) {
          console.error(`Error fetching county for user ${userId}:`, error);
          return null;
        }
      });

      const validResponses = (await Promise.all(userCountyChecks)).filter(
        Boolean
      );

      // console.log("Valid Responses:", validResponses.map((doc) => doc.data().user_id));

      let counts = {
        disasterImpactCount: {
          affected: 0,
          notAffected: 0,
          notApplicable: 0,
          noAnswer: 0,
        },
        householdMembersCount: 0,
        injuryCount: {
          noInjury: 0,
          minorInjury: 0,
          medicalTreatment: 0,
          visitedHospital: 0,
          admitted: 0,
          notApplicable: 0,
          noAnswer: 0,
        },
        homeDamageCount: {
          noDamage: 0,
          slightDamage: 0,
          moderateDamage: 0,
          severeDamage: 0,
          totalDestruction: 0,
          notApplicable: 0,
          noAnswer: 0,
        },
        homesDestroyedCount: {
          undamaged: 0,
          damaged: 0,
          destroyed: 0,
          notApplicable: 0,
          noAnswer: 0,
        },
        currentLivingSituationCount: {
          safely: 0,
          unsafely: 0,
          displacedRelative: 0,
          displacedHotel: 0,
          unsheltered: 0,
          notApplicable: 0,
          noAnswer: 0,
        },
        typesOfHousingCount: {
          singleFamilyDwelling: 0,
          mobileManufactured: 0,
          condo: 0,
          other: 0,
          notApplicable: 0,
          noAnswer: 0,
        },
        typeOfPropertyDamageCount: {
          Siding: 0,
          Flooring: 0,
          Cabinets: 0,
          Shed: 0,
          Roof: 0,
          Foundation: 0,
          "Floor Joists": 0,
          HVAC: 0,
          Driveway: 0,
          Gutters: 0,
          Ceiling: 0,
          Drywall: 0,
          Electrical: 0,
          "Yard/Uprooted trees": 0,
          Basement: 0,
          "Attached Garage": 0,
          "Detached Garage": 0,
          Carport: 0,
          Other: 0,
          "N/A": 0,
        },
        immediateNeedsCount: {
          "Food / nutrition": 0,
          "Employment/Job training": 0,
          "Utilities (shut-off or pending shut-off)": 0,
          "Medical health care": 0,
          Transportation: 0,
          "Furniture, appliances": 0,
          Medication: 0,
          "Child care": 0,
          Clothing: 0,
          Counseling: 0,
          "Financial Assistance": 0,
          "Resource Information": 0,
          "Personal Hygiene": 0,
          "Medical Equipment": 0,
          "Volunteer Support (labor/debris)": 0,
          "Home repair": 0,
          Eviction: 0,
          Water: 0,
          "Mold Remediation": 0,
          "Temporary Housing": 0,
          Other: 0,
          "N/A": 0,
        },
        mobilityMedicalNeedsCount: {
          haveElectricity: 0,
          noElectricity: 0,
          none: 0,
          notApplicable: 0,
          noAnswer: 0,
        },
        shelterDuringTornadoCount: {
          inHome: 0,
          stormShelter: 0,
          basement: 0,
          friendFamily: 0,
          publicShelter: 0,
          noShelter: 0,
          notApplicable: 0,
          noAnswer: 0,
        },
        waterDepthCount: {
          none: 0,
          lessThan1Inch: 0,
          oneToSixInches: 0,
          sevenToSeventeenInches: 0,
          eighteenToTwentyFourInches: 0,
          moreThanTwoFeet: 0,
          ceilingOrHigher: 0,
          notApplicable: 0,
          noAnswer: 0,
        },
        vehiclesAffected: 0,
        transportationCount: { yes: 0, no: 0 },
        wellWaterCount: { yes: 0, no: 0 },
        displacedCount: { isDisplaced: 0, isNotDisplaced: 0 },
        ownerRenterCount: { Renter: 0, Owner: 0, RentToOwn: 0, noAnswer: 0 },
        insuranceTypeCount: {
          floodInsurance: 0,
          earthquakeCoverage: 0,
          otherCoverage: 0,
          noAdditionalInsurance: 0,
          noAnswer: 0,
        },
      };

      const onGetHouseholdMembers = async (id, disater_id) => {
        const householdMembersRef = collection(
          db,
          "users",
          id,
          "disaster_households",
          disater_id,
          "household_members"
        );
        const householdMembers = await getDocs(householdMembersRef);

        return householdMembers.docs.length;
      };

      validResponses.forEach(async (doc) => {
        const data = doc.data();
        const getAnswer = (key) => data.responses?.[key]?.answer || "";

        if (
          getAnswer("Was the impacted address your primary address?") ===
            "Yes" ||
          getAnswer("Was the impacted address your primary address?") === "No"
        ) {
          const getHouseholdMembersCount = await onGetHouseholdMembers(
            data.user_id,
            disasterId
          );

          counts.householdMembersCount += getHouseholdMembersCount;
        }

        // Disaster Impact
        switch (getAnswer("Was the impacted address your primary address?")) {
          case "Yes":
            counts.disasterImpactCount.affected += 1;
            break;
          case "No":
            counts.disasterImpactCount.notAffected += 1;
            break;
          case "N/A":
            counts.disasterImpactCount.notApplicable += 1;
            break;
          default:
            counts.disasterImpactCount.noAnswer += 1;
            break;
        }

        // Injury Data Mapping
        switch (
          getAnswer(
            "What is the worst level of injury sustained by a member of your household?"
          )
        ) {
          case "No Injury":
            counts.injuryCount.noInjury += 1;
            break;
          case "Minor Injury":
            counts.injuryCount.minorInjury += 1;
            break;
          case "Sought Medical Treatment":
            counts.injuryCount.medicalTreatment += 1;
            break;
          case "Visited Hospital":
            counts.injuryCount.visitedHospital += 1;
            break;
          case "Admitted":
            counts.injuryCount.admitted += 1;
            break;
          case "N/A":
            counts.injuryCount.notApplicable += 1;
            break;
          default:
            counts.injuryCount.noAnswer += 1;
            break;
        }

        // Home Damage Mapping
        switch (
          getAnswer("How would you describe the level of damage to your home?")
        ) {
          case "No Damage":
            counts.homeDamageCount.noDamage += 1;
            break;
          case "Slight Damage":
            counts.homeDamageCount.slightDamage += 1;
            break;
          case "Moderate Damage":
            counts.homeDamageCount.moderateDamage += 1;
            break;
          case "Severe Damage":
            counts.homeDamageCount.severeDamage += 1;
            break;
          case "Total Destruction":
            counts.homeDamageCount.totalDestruction += 1;
            break;
          case "N/A":
            counts.homeDamageCount.notApplicable += 1;
            break;
          default:
            counts.homeDamageCount.noAnswer += 1;
            break;
        }

        // Homes Destroyed Mapping
        switch (getAnswer("The impacted home:")) {
          case "Has minor damage":
            counts.homesDestroyedCount.undamaged += 1;
            break;
          case "Has major damage":
            counts.homesDestroyedCount.damaged += 1;
            break;
          case "Is destroyed":
            counts.homesDestroyedCount.destroyed += 1;
            break;
          case "N/A":
            counts.homesDestroyedCount.notApplicable += 1;
            break;
          default:
            counts.homesDestroyedCount.noAnswer += 1;
            break;
        }

        // Current Living Situation
        switch (getAnswer("What is your current living situation?")) {
          case "I am safely living inside my home.":
            counts.currentLivingSituationCount.safely += 1;
            break;
          case "I am unsafely living inside my home.":
            counts.currentLivingSituationCount.unsafely += 1;
            break;
          case "I am displaced with at the residence of a relative or friend.":
            counts.currentLivingSituationCount.displacedRelative += 1;
            break;
          case "I am displaced at a hotel or other form of temporary housing or shelter.":
            counts.currentLivingSituationCount.displacedHotel += 1;
            break;
          case "I am unsheltered.":
            counts.currentLivingSituationCount.unsheltered += 1;
            break;
          case "N/A":
            counts.currentLivingSituationCount.notApplicable += 1;
            break;
          default:
            counts.currentLivingSituationCount.noAnswer += 1;
            break;
        }

        // Types of Housing
        switch (getAnswer("What type of structure is the impacted home?")) {
          case "Single family dwelling":
            counts.typesOfHousingCount.singleFamilyDwelling += 1;
            break;
          case "Mobile or manufactured":
            counts.typesOfHousingCount.mobileManufactured += 1;
            break;
          case "Condo":
            counts.typesOfHousingCount.condo += 1;
            break;
          case "Other":
            counts.typesOfHousingCount.other += 1;
            break;
          case "N/A":
            counts.typesOfHousingCount.notApplicable += 1;
            break;
          default:
            counts.typesOfHousingCount.noAnswer += 1;
            break;
        }

        // Mobility/Medical Needs
        switch (
          getAnswer(
            "Do you or anyone in your household have mobility or medical equipment that requires electricity?"
          )
        ) {
          case "Yes, and I currently have electricity.":
            counts.mobilityMedicalNeedsCount.haveElectricity += 1;
            break;
          case "Yes, and I currently do not have electricity.":
            counts.mobilityMedicalNeedsCount.noElectricity += 1;
            break;
          case "No.":
            counts.mobilityMedicalNeedsCount.none += 1;
            break;
          case "N/A":
            counts.mobilityMedicalNeedsCount.notApplicable += 1;
            break;
          default:
            counts.mobilityMedicalNeedsCount.noAnswer += 1;
            break;
        }

        // Shelter During Tornado
        switch (getAnswer("Did you have to take shelter during the tornado?")) {
          case "Yes, in my home":
            counts.shelterDuringTornadoCount.inHome += 1;
            break;
          case "Yes, in my storm shelter":
            counts.shelterDuringTornadoCount.stormShelter += 1;
            break;
          case "Yes, in my basement":
            counts.shelterDuringTornadoCount.basement += 1;
            break;
          case "Yes, at a friend or family members":
            counts.shelterDuringTornadoCount.friendFamily += 1;
            break;
          case "Yes, in a public storm shelter":
            counts.shelterDuringTornadoCount.publicShelter += 1;
            break;
          case "No, I did not take shelter":
            counts.shelterDuringTornadoCount.noShelter += 1;
            break;
          case "N/A":
            counts.shelterDuringTornadoCount.notApplicable += 1;
            break;
          default:
            counts.shelterDuringTornadoCount.noAnswer += 1;
            break;
        }

        const propertyDamageMap =
          data.responses?.["What parts of the property had damage?"]?.answer ||
          {};
        Object.keys(counts.typeOfPropertyDamageCount).forEach((type) => {
          if (propertyDamageMap[type] === true) {
            counts.typeOfPropertyDamageCount[type] += 1;
          }
        });

        // Processing 'immediateNeeds'
        const immediateNeedsMap =
          data.responses?.["IMMEDIATE NEEDS Check all that apply"]?.answer ||
          {};
        Object.keys(counts.immediateNeedsCount).forEach((need) => {
          if (immediateNeedsMap[need] === true) {
            counts.immediateNeedsCount[need] += 1;
          }
        });

        // Record water depth
        switch (
          getAnswer(
            "What is the estimated depth of water that entered the home?"
          )
        ) {
          case "No water entered the home":
            counts.waterDepthCount.none += 1;
            break;
          case "Less than 1 inch (e.g., damp floor, minor seepage)":
            counts.waterDepthCount.lessThan1Inch += 1;
            break;
          case "1 to 6 inches (e.g., shallow flooding on floor level)":
            counts.waterDepthCount.oneToSixInches += 1;
            break;
          case "7 to 17 inches (e.g., above floorboards, lower wall damage)":
            counts.waterDepthCount.sevenToSeventeenInches += 1;
            break;
          case "18 to 24 inches (e.g., electrical outlets impacted, significant damage possible)":
            counts.waterDepthCount.eighteenToTwentyFourInches += 1;
            break;
          case "More than 24 inches / 2 feet (e.g., major structural damage possible)":
            counts.waterDepthCount.moreThanTwoFeet += 1;
            break;
          case "Water reached ceiling or higher floor(s)":
            counts.waterDepthCount.ceilingOrHigher += 1;
            break;
          case "N/A":
            counts.waterDepthCount.notApplicable += 1;
            break;
          default:
            counts.waterDepthCount.noAnswer += 1;
            break;
        }

        // Total No. of Vehicles Affected
        const vehicleNumber = parseInt(
          getAnswer("How many vehicles were impacted?"),
          10
        );
        if (!isNaN(vehicleNumber)) {
          counts.vehiclesAffected += vehicleNumber;
        }

        // Needs Reliable Transportation
        switch (getAnswer("Do you have access to reliable transportation?")) {
          case "Yes":
            counts.transportationCount.yes += 1;
            break;
          case "No":
            counts.transportationCount.no += 1;
            break;
          default:
            break;
        }

        // Citizens with Well Water
        switch (getAnswer("Do you have well water?")) {
          case "Yes":
            counts.wellWaterCount.yes += 1;
            break;
          case "No":
            counts.wellWaterCount.no += 1;
            break;
          default:
            break;
        }
      });

      const householdIdPromises = validResponses.map(async (response) => {
        try {
          const userDocRef = doc(db, "users", response.data().user_id);
          const userSnapshot = await getDoc(userDocRef);
          if (userSnapshot.exists()) {
            const userData = userSnapshot.data();
            return {
              household_id: userData.household_id,
              user_details: {
                first_name: userData.first_name || "",
                last_name: userData.last_name || "",
                email: userData.email || "",
                mobile_number: userData.mobile_number || "",
                address: [
                  userData.address1 || "",
                  userData.address2 || "",
                  userData.city || "",
                  userData.state || "",
                  userData.zip || "",
                  userData.county || "",
                ]
                  .filter(Boolean)
                  .join(" "),
              },
            };
          }
        } catch (error) {
          console.error(
            "Error fetching household_id for user",
            response.data().user_id,
            error
          );
        }
        return null;
      });

      const householdIds = (await Promise.all(householdIdPromises)).filter(
        Boolean
      );
      const uniqueHouseholds = [
        ...new Map(
          householdIds.map((item) => [item.household_id, item])
        ).values(),
      ];

      const userPromises = validResponses.map(async (surveyDoc) => {
        const userDetails = await getDoc(
          doc(db, "users", surveyDoc.data().user_id)
        );
        const userDetailsData = userDetails.data();

        if (userDetailsData.is_displaced) {
          counts.displacedCount.isDisplaced += 1;
        } else {
          counts.displacedCount.isNotDisplaced += 1;
        }

        const householdDetails = await getDoc(
          doc(
            db,
            "users",
            surveyDoc.data().user_id,
            "disaster_households",
            disasterId,
            "household_info",
            "details"
          )
        );
        const householdData = householdDetails.data();
        if (householdData) {
          const ownerOrRenter = householdData.owner_or_renter;
          if (ownerOrRenter === "Renter") {
            counts.ownerRenterCount.Renter += 1;
          } else if (ownerOrRenter === "Owner") {
            counts.ownerRenterCount.Owner += 1;
          } else if (ownerOrRenter === "Rent to Own") {
            counts.ownerRenterCount.RentToOwn += 1;
          } else {
            counts.ownerRenterCount.noAnswer += 1;
          }

          const insuranceType = householdData.additional_insurance_type;
          switch (insuranceType) {
            case "Flood insurance":
              counts.insuranceTypeCount.floodInsurance += 1;
              break;
            case "Earthquake additional coverage":
              counts.insuranceTypeCount.earthquakeCoverage += 1;
              break;
            case "Other disaster-related additional coverage":
              counts.insuranceTypeCount.otherCoverage += 1;
              break;
            case "I did not have additional insurance":
              counts.insuranceTypeCount.noAdditionalInsurance += 1;
              break;
            default:
              counts.insuranceTypeCount.noAnswer += 1;
              break;
          }
        } else {
          counts.ownerRenterCount.noAnswer += 1;
          counts.insuranceTypeCount.noAnswer += 1;
        }
      });

      await Promise.all(userPromises);

      return {
        ...counts,
        totalRecords: validResponses.length,
        totalHouseholdAffected: uniqueHouseholds.length,
        affectedHouseholds: uniqueHouseholds.map((h) => h.user_details),
        affectedUserIds: validResponses.map((doc) => doc.data().user_id),
      };
    } catch (error) {
      console.error("Error fetching disaster survey data:", error);
      throw error;
    }
  },

  fetchAllData: async () => {
    try {
      const mainCollectionRef = collection(db, "disaster-mgt");
      const mainSnapshot = await getDocs(mainCollectionRef);

      const allData = [];

      for (const docSnap of mainSnapshot.docs) {
        const docId = docSnap.id;

        const subcollections = [
          "general",
          "home_repair",
          "landscaping",
          "vehicle",
          "contents",
        ];

        for (const subcollectionName of subcollections) {
          const subcollectionRef = collection(
            db,
            "disaster-mgt",
            docId,
            subcollectionName
          );
          const subcollectionSnapshot = await getDocs(subcollectionRef);

          const subcollectionData = subcollectionSnapshot.docs.map(
            (subDoc) => ({
              id: subDoc.id,
              ...subDoc.data(),
            })
          );

          allData.push({
            docId,
            subcollectionName,
            data: subcollectionData,
          });

          console.log(
            `Fetched data from subcollection "${subcollectionName}" of document "${docId}"`,
            subcollectionData
          );
        }
      }

      return allData;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  },

  fetchRecords: async (userId, collectionName) => {
    try {
      const documentsCollectionRef = collection(
        db,
        "disaster-mgt",
        userId,
        collectionName
      );
      const documentsSnapshot = await getDocs(documentsCollectionRef);

      const documentsList = documentsSnapshot.docs.map((doc) => {
        const docData = doc.data();
        return {
          ...docData,
          id: doc.id,
        };
      });

      return documentsList;
    } catch (error) {
      console.error("Error fetching documents:", error);
      throw error;
    }
  },
  addRecord: async (userId, documentData, collectionName) => {
    try {
      console.log("addRecord called with:", {
        userId,
        documentData,
        collectionName,
      });

      if (!userId) throw new Error("userId is undefined");
      if (!collectionName) throw new Error("collectionName is undefined");
      if (!documentData) throw new Error("documentData is undefined");

      const documentsCollectionRef = collection(
        db,
        "disaster-mgt",
        userId,
        collectionName
      );

      console.log("Collection reference created:", documentsCollectionRef);

      const updatedDocumentData = {
        ...documentData,
        userId: userId,
        updated_at: serverTimestamp(),
      };

      console.log("Attempting to add document with data:", updatedDocumentData);

      const docRef = await addDoc(documentsCollectionRef, updatedDocumentData);

      console.log("Document successfully added with ID:", docRef.id);
    } catch (error) {
      console.error("Error adding document: ", error);
      throw error;
    }
  },

  updateRecord: async (userId, documentId, documentData, collectionName) => {
    try {
      const documentsCollectionRef = collection(
        db,
        "disaster-mgt",
        userId,
        collectionName
      );

      const idField = getIdFieldForCollection(collectionName);

      const q = query(documentsCollectionRef, where(idField, "==", documentId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        querySnapshot.forEach(async (doc) => {
          const documentRef = doc.ref;

          const updatedDocumentData = {
            ...documentData,
            userId: userId,
            updated_at: serverTimestamp(),
          };

          await updateDoc(documentRef, updatedDocumentData);
        });
        console.log("Document successfully updated!");
      } else {
        console.error("No document found with the given ID to update.");
        throw new Error("No document found with the given ID to update.");
      }
    } catch (error) {
      console.error("Error updating document: ", error);
      throw error;
    }
  },

  deleteRecord: async (userId, documentId, collectionName) => {
    try {
      console.log("Deleting document with ID:", documentId);

      if (!documentId) {
        throw new Error("documentId is undefined or null");
      }

      const documentsCollectionRef = collection(
        db,
        "disaster-mgt",
        userId,
        collectionName
      );
      const idField = getIdFieldForCollection(collectionName);

      const q = query(documentsCollectionRef, where(idField, "==", documentId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        querySnapshot.forEach(async (doc) => {
          const documentRef = doc.ref;
          await deleteDoc(documentRef);
        });
        console.log("Document successfully deleted!");
      } else {
        console.error("No document found with the given ID to delete.");
        throw new Error("No document found with the given ID to delete.");
      }
    } catch (error) {
      console.error(
        "Error deleting document from disaster management: ",
        error
      );
      throw error;
    }
  },

  getLastEditedTime: async (userId, collectionName) => {
    try {
      const documentsCollectionRef = collection(
        db,
        "disaster-mgt",
        userId,
        collectionName
      );
      const q = query(
        documentsCollectionRef,
        orderBy("last_edited", "desc"),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data().last_edited.toDate();
      }
      return null;
    } catch (error) {
      console.error("Error getting last edited time:", error);
      return null;
    }
  },
};

const getIdFieldForCollection = (collectionName) => {
  switch (collectionName) {
    case "general":
      return "generalID";
    case "home_repair":
      return "homeRepairID";
    case "landscaping":
      return "landscapingID";
    case "contents":
      return "contentsID";
    case "vehicle":
      return "vehicleID";
    default:
      throw new Error(`Unknown collection name: ${collectionName}`);
  }
};

export default DisasterMgtService;
